import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './SearchResults.css';

export default function SearchResults({ results, query, onClose, inputRef }) {
  const navigate    = useNavigate();
  const dropdownRef = useRef(null);
  const itemRefs    = useRef([]);

  // ── Mobile: pin the fixed dropdown directly below the sticky header ──────
  // We measure the header's bottom edge rather than the input's, because the
  // input lives inside a max-height animated container and its getBoundingClientRect
  // can fire before the animation finishes, landing the dropdown in the wrong spot.
  const [mobileTop, setMobileTop] = useState(undefined);

  useEffect(() => {
    function calcTop() {
      if (window.innerWidth > 768) {
        setMobileTop(undefined);
        return;
      }
      // Walk up from the input to find the <header> element
      let el = inputRef?.current;
      while (el && el.tagName !== 'HEADER') el = el.parentElement;
      if (el) {
        setMobileTop(`${el.getBoundingClientRect().bottom}px`);
      } else if (inputRef?.current) {
        // Fallback: use the input itself
        setMobileTop(`${inputRef.current.getBoundingClientRect().bottom + 8}px`);
      }
    }

    // Delay slightly to let the expand animation finish (250ms in CSS)
    const t = setTimeout(calcTop, 260);
    window.addEventListener('resize', calcTop);
    return () => {
      clearTimeout(t);
      window.removeEventListener('resize', calcTop);
    };
  }, [inputRef]);

  // ── Close on outside click ───────────────────────────────────────────────
  useEffect(() => {
    function handleClick(e) {
      if (
        dropdownRef.current && !dropdownRef.current.contains(e.target) &&
        inputRef?.current   && !inputRef.current.contains(e.target)
      ) {
        onClose();
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [onClose, inputRef]);

  // ── Keyboard: ↑ ↓ Escape ────────────────────────────────────────────────
  useEffect(() => {
    function handleKeyDown(e) {
      const focused = document.activeElement;
      const idx     = itemRefs.current.findIndex(el => el === focused);

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        const next = idx < results.length - 1 ? idx + 1 : 0;
        itemRefs.current[next]?.focus();
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        const prev = idx > 0 ? idx - 1 : results.length - 1;
        itemRefs.current[prev]?.focus();
      } else if (e.key === 'Escape') {
        onClose();
        inputRef?.current?.blur();
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [results, onClose, inputRef]);

  const handleSelect = (product) => {
    navigate(product.path, product.state ? { state: product.state } : undefined);
    onClose();
  };

  const dropdownStyle = mobileTop !== undefined ? { top: mobileTop } : {};

  if (!results.length) {
    return (
      <div className="sr-dropdown" ref={dropdownRef} style={dropdownStyle}>
        <div className="sr-empty">
          <span className="sr-empty-icon">🔍</span>
          <p>No results for <strong>"{query}"</strong></p>
          <span>Try a different keyword or browse our categories</span>
        </div>
      </div>
    );
  }

  const grouped = results.reduce((acc, product) => {
    if (!acc[product.category]) acc[product.category] = [];
    acc[product.category].push(product);
    return acc;
  }, {});

  let globalIdx = 0;

  return (
    <div className="sr-dropdown" ref={dropdownRef} role="listbox" aria-label="Search results" style={dropdownStyle}>
      {Object.entries(grouped).map(([category, products]) => (
        <div key={category} className="sr-group">
          <div className="sr-group-label">{category}</div>
          {products.map((product) => {
            const idx = globalIdx++;
            return (
              <button
                key={product.id}
                ref={el => (itemRefs.current[idx] = el)}
                className="sr-item"
                role="option"
                onClick={() => handleSelect(product)}
                onKeyDown={e => e.key === 'Enter' && handleSelect(product)}
              >
                <span className="sr-item-icon">
                  <SearchIcon />
                </span>
                <div className="sr-item-text">
                  <span className="sr-item-name">
                    <HighlightMatch text={product.name} query={query} />
                  </span>
                  <span className="sr-item-sub">
                    {product.subCategory
                      ? `${product.subCategory} · ${product.category}`
                      : product.category}
                  </span>
                </div>
                <span className="sr-item-arrow">›</span>
              </button>
            );
          })}
        </div>
      ))}
      <div className="sr-footer">
        {results.length} result{results.length !== 1 ? 's' : ''}
      </div>
    </div>
  );
}

function HighlightMatch({ text, query }) {
  if (!query) return <>{text}</>;
  const q   = query.trim();
  const idx = text.toLowerCase().indexOf(q.toLowerCase());
  if (idx === -1) return <>{text}</>;
  return (
    <>
      {text.slice(0, idx)}
      <mark className="sr-highlight">{text.slice(idx, idx + q.length)}</mark>
      {text.slice(idx + q.length)}
    </>
  );
}

function SearchIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
      <circle cx="11" cy="11" r="8"/>
      <path d="M21 21l-4.35-4.35"/>
    </svg>
  );
}